/**
 * useNotifications.ts — Web Push subscription management hook.
 * Handles SW registration, VAPID key fetching, subscribe/unsubscribe lifecycle.
 */

import { useState, useEffect, useCallback } from 'react'
import { API_BASE } from '../utils/api'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)))
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePushNotifications() {
  const [supported, setSupported] = useState(false)
  const [permission, setPermission] = useState<NotificationPermission>('default')
  const [subscribed, setSubscribed] = useState(false)
  const [loading, setLoading] = useState(true)

  // ── Check support & initial state on mount ────────────────────────────────
  useEffect(() => {
    const isSupported =
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window

    setSupported(isSupported)

    if (!isSupported) {
      setLoading(false)
      return
    }

    setPermission(Notification.permission)

    // Check if there's already an active push subscription
    const checkExistingSubscription = async () => {
      try {
        const registration = await navigator.serviceWorker.ready
        const existingSub = await registration.pushManager.getSubscription()
        setSubscribed(!!existingSub)
      } catch (err) {
        console.warn('Could not check existing push subscription:', err)
      } finally {
        setLoading(false)
      }
    }

    checkExistingSubscription()
  }, [])

  // ── Subscribe ─────────────────────────────────────────────────────────────
  const subscribe = useCallback(async () => {
    if (!supported) return
    setLoading(true)

    try {
      // 1. Register (or get existing) service worker
      const registration = await navigator.serviceWorker.register('/sw.js')
      await navigator.serviceWorker.ready

      // 2. Request notification permission
      const perm = await Notification.requestPermission()
      setPermission(perm)
      if (perm !== 'granted') {
        setLoading(false)
        return
      }

      // 3. Fetch VAPID public key from backend
      const res = await fetch(`${API_BASE}/api/notify/vapid-public-key`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('lamoney-token') || ''}` },
      })
      if (!res.ok) throw new Error('Could not fetch VAPID public key')
      const { publicKey: vapidPublicKey } = await res.json()

      // 4. Subscribe via PushManager
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      })

      // 5. Save subscription to backend
      const token = localStorage.getItem('lamoney-token')
      const saveRes = await fetch(`${API_BASE}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(sub.toJSON()),
      })

      if (!saveRes.ok) throw new Error('Could not save push subscription')

      setSubscribed(true)
    } catch (err) {
      console.error('Push subscription failed:', err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [supported])

  // ── Unsubscribe ───────────────────────────────────────────────────────────
  const unsubscribe = useCallback(async () => {
    if (!supported) return
    setLoading(true)

    try {
      const registration = await navigator.serviceWorker.ready
      const existingSub = await registration.pushManager.getSubscription()

      if (existingSub) {
        // Notify backend first, then remove locally
        const token = localStorage.getItem('lamoney-token')
        await fetch(`${API_BASE}/api/push/unsubscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ endpoint: existingSub.endpoint }),
        }).catch(() => {
          /* best-effort */
        })

        await existingSub.unsubscribe()
      }

      setSubscribed(false)
    } catch (err) {
      console.error('Push unsubscribe failed:', err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [supported])

  return { supported, permission, subscribed, loading, subscribe, unsubscribe }
}
